/**
 * Status glyph shared by the Document Library list and the home Recent
 * Documents/Scopes rows: render the `InlineStatusIndicator` when the
 * (type, status) pair maps to a status, otherwise the canonical library type
 * icon. One component so every surface shows the identical glyph rather than
 * drifting fallbacks.
 */
import type { FileType } from "@/api";
import {
    getDocumentFileTypeIcon,
    getLibraryItemIcon,
    type LibraryItemTypeKey,
} from "@/pages/frontend/documents/utils/libraryItemTypes";

import { InlineStatusIndicator } from "./InlineStatusIndicator";
import {
    getStatusConfig,
    type StatusItemType,
} from "./inlineStatusIndicator.config";

export function InlineStatusIndicatorOrIcon({
    type,
    status,
    typeKey,
    fileType,
}: {
    type: StatusItemType;
    status: string | null;
    typeKey: LibraryItemTypeKey;
    /** Document rows render a per-`file_type` glyph (spreadsheet vs PDF). */
    fileType?: FileType | null;
}) {
    // `InlineStatusIndicator` returns null when there is no status mapping, so
    // resolve the config here to render the indicator OR the icon, never both.
    const config = getStatusConfig(type, status);
    if (config) return <InlineStatusIndicator type={type} status={status} />;
    // A document shows a file-type glyph so a spreadsheet is distinguishable
    // from a PDF at a glance; folders/scopes/comparisons keep their type icon.
    if (typeKey === "document") return <>{getDocumentFileTypeIcon(fileType)}</>;
    return <>{getLibraryItemIcon(typeKey)}</>;
}
